import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaFileDownload, FaShare } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { FaDownload } from "react-icons/fa6";
import { CiFileOn, CiImageOn } from "react-icons/ci";
import styles from "../../styles/patient-page-modules/PatientDocuments.module.css";
import medicalReport from "../../assets/images/medical-report.png";
import DateRangeSelector from "../../components/dateRangeSelector";
import auditReport from "../../assets/images/report.png";
import Button from "../../components/button-v1";
import { BASE_URL } from "../../utils/constants";
import { buildReportFilename, DOCTYPES } from "../../utils/fileNaming";
import { Suspense } from "react";
import Spinner from "../../components/spinner";
import { Dropdown, DropdownItem } from "../../components/ui/dropdown";
import Table from "../../components/table";
import { formatDate, formatISODate, formatMonthYear } from "../../utils/helper";
import { getCarePlan } from "../../services/patient/carePlan";
import { getPatientGeneralDetails } from "../../services/patient/details";
import {
  uploadPatientDocuments,
  getPatientDocuments,
  deletePatientDocument,
  duplicatePatientDocument,
  replacePatientDocument
} from "../../services/patient/documents";
import {
  processDocumentsForAI,
  extractDocumentIds,
} from "../../services/patient/rag";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { parseDateSafe } from "../../utils/dateFormatters";
import { generateYears } from "../../utils/constants";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue
} from "../../components/ui/select";
import {
  Panel,
  TabList,
  TabPanels,
  TabTrigger,
  Tabs
} from "../../components/ui/tab";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../../components/ui/dialog";
import UploadFiles from "../../components/shared-modules/upload-files/index";
import { toast } from "react-hot-toast";
import { months } from "../../utils/constants";
import AlertDialog from "../../components/alert-dialogue";

const DownloadReadingDialogue = React.lazy(() =>
  import("../../components/demo-download-dialogue")
);

const SharingModal = React.lazy(() =>
  import("../../components/shared-modules/share-modal")
);

const shareOptions = [
  { label: "PDF", value: "pdf", icon: <CiFileOn /> },
  { label: "PNG", value: "png", icon: <CiImageOn /> }
];

const PatientDocuments = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [sharing, setSharing] = useState(false);
  const [isSharingModalOpen, setIsShareingModalOpen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const patientId = sessionStorage.getItem("_pt_id");
  const CCM = Boolean(Number.parseInt(queryParams.get("CCM") || "0"));
  const RPM = Boolean(Number.parseInt(queryParams.get("RPM") || "0"));
  const [documentFilter, setDocumentFilter] = useState(["all"]);
  const [activeSection, setActiveSection] = useState("RPM");
  const [uploadedFiles, setUploadedFiles] = useState("");
  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);
  const [readingDateRange, setReadingDateRange] = useState({
    start: "",
    end: ""
  });

  const [loading, setLoading] = useState({ pdf: false, png: false });
  const [isOpen, setIsOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(1); // 1: upload, 2: conflict resolution
  const [matchedDocuments, setMatchedDocuments] = useState([]); // Array of { file, existingDoc, operation: 'add' | 'replace' }
  const [processingDocuments, setProcessingDocuments] = useState([]); // Track which documents are being processed: [{index, operation}]

const careplanLisTQuery = useQuery(["patient-careplan-list", patientId],
  () => getCarePlan()
);

const { data: patient } = useQuery(
  ["patient-general-details", patientId],
  () => getPatientGeneralDetails(patientId)
);


  const patientDocumentsMutation = useMutation(
    async ({ newFiles, matchedFiles }) => {
      // Tag each task by kind so we know which resulting document ids to queue
      // for AI processing: brand-new uploads and "replace" (content changed),
      // but NOT plain "add" duplicates.
      const tasks = [];

      // 1. Handle new files (batch upload)
      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach((file, index) => {
          formData.append("documents", file.file);
          formData.append(`document_type_${index}`, file.type);
        });
        tasks.push(uploadPatientDocuments(formData).then((r) => ({ kind: "new", r })));
      }

      // 2. Handle matched files (individual operations)
      matchedFiles.forEach(match => {
        const formData = new FormData();
        formData.append("documents", match.file);
        formData.append("document_type", match.type);

        if (match.operation === "add") {
          tasks.push(
            duplicatePatientDocument(match.existingDoc.id, formData).then((r) => ({ kind: "dup", r }))
          );
        } else if (match.operation === "replace") {
          tasks.push(
            replacePatientDocument(match.existingDoc.id, formData).then((r) => ({ kind: "replace", r }))
          );
        }
      });

      const results = await Promise.all(tasks);

      // Queue new + replaced documents for AI processing (fire-and-forget; a
      // failure here must not affect the upload's success state).
      const aiIds = results
        .filter((x) => x.kind === "new" || x.kind === "replace")
        .flatMap((x) => extractDocumentIds(x.r));
      processDocumentsForAI(aiIds);

      return results;
    },
    {
      onSuccess: () => {
        setUploadedFiles([]);
        setMatchedDocuments([]);
        setUploadStep(1);
        setShowUploadModal(false);
        toast.success("Documents uploaded successfully");
        queryClient.invalidateQueries(["patient-documents"]);
      },
      onError: error => {
        toast.error("Could not upload documents.");
      }
    }
  );

  const patientDocumenTDeleteMutation = useMutation(
    id => deletePatientDocument(id),
    {
      onSuccess: () => {
        toast.success("Document Removed successfully");
        setDeleteDocumentId(null);
        setShowDeleteDialogue(false);
        queryClient.invalidateQueries(["patient-documents"]);
      },
      onError: () => {
        toast.error("Could not delete document");
        setDeleteDocumentId(null);
      }
    }
  );

  const patientDocumentsQuery = useQuery(["patient-documents", patientId], () =>
    getPatientDocuments()
  );


  // Auto-close modal when all conflicts are resolved and no files remain
  useEffect(() => {
    if (
      uploadStep === 2 &&
      matchedDocuments.length === 0 &&
      uploadedFiles.length === 0
    ) {
      setShowUploadModal(false);
      setUploadStep(1);
      toast.success("All documents processed successfully");
    }
  }, [uploadStep, matchedDocuments.length, uploadedFiles.length]);

  // Table columns configuration for CCM documents
  const ccmColumns = [
    // { key: "filename", label: "Filename", alignLeft: true, sorting: false },
    {
      key: "date",
      label: "Month",
      alignLeft: true,
      sorting: false,
      render: row => (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {row.careplan_document_url && (
            <img
              src="/assets/images/svgs/hugeicons_pdf-02.svg"
              alt="PDF"
              style={{ width: "14px", height: "14px" }}
            />
          )}
          {formatMonthYear(row.created_at)}
        
        </span>
      )
    },

    {
      key: "status",
      label: "Status",
      alignLeft: true,
      sorting: false,
      render: row => (
        <span 
          className={styles.status_tag}
          data-status={row.status?.toLowerCase()}
        >
          {row.status ? row.status.replace(/_/g, ' ') : "N/A"}
        </span>
      )
    },
     {
      key: "created_at",
      label: "Created At",
      alignLeft: true,
      sorting: false,
      render: row => formatDate(row.created_at)
    },



    {
      key: "action",
      label: "Action",
      alignLeft: false,
      sorting: false,
      render: row => (
        <Button
          variant="text"
          className={styles.download_action_btn}
          onClick={e => {
            e.stopPropagation();

            if (row.careplan_document_url) {
              window.open(row.careplan_document_url, "_blank", "noopener,noreferrer");
              return;
            }

            const date = parseDateSafe(row.created_at) || new Date();
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const newTabUrl = `/patient/care-plan-pdf?month=${month}&year=${year}`;
            window.open(newTabUrl, "_blank");
          }}
        >
          <FaFileDownload /> Download
        </Button>
      )
    }
  ];

  const patientDocumentsColumn = [
    // { key: "filename", label: "Filename", alignLeft: true, sorting: false },
    {
      key: "date",
      label: "Date",
      alignLeft: true,
      sorting: false,
      render: row => (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {formatDate(row.uploaded_at)}
         
        </span>
      )
    },
    {
      key: "document_name",
      label: "Name",
      alignLeft: true,
      sorting: true,
      render: row => row.document_name
    },
    {
      key: "document_type",
      label: "Type",
      alignLeft: true,
      sorting: true,
      render: row => row.document_type
    },
    {
      key: "action",
      label: "Action",
      alignLeft: false,
      sorting: false,
      render: row => (
        <section className={styles.action_container}>
          {row.document_url && (
            <Button
              variant="ghost"
              className={styles.download_action_btn}
              onClick={e => {
                // e.stopPropagation();
                const link = document.createElement("a");
                link.href = row.document_url;
                link.target = "_blank"; // new tab
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
            >
              <FaDownload size={14} />
            </Button>
          )}
          <Button
            variant="ghost"
            className={styles.download_action_btn}
            onClick={e => {
              // e.stopPropagation();
              // e.preventDefault();
              setDeleteDocumentId(row.id);
              setShowDeleteDialogue(true);
            }}
            loading={patientDocumenTDeleteMutation.isLoading}
          >
            <MdDelete size={14} />
          </Button>
        </section>
      )
    }
  ];

  const handleDateChange = newRange => {
    newRange.start = formatISODate(newRange.start);
    newRange.end = formatISODate(newRange.end);
    setReadingDateRange(newRange);
  };

  const handleDownload = async (dateRange, format) => {
    if (!format) {
      toast.error("Please select a format before downloading.");
      return;
    }
    if (!dateRange?.start || !dateRange?.end) {
      toast.error("Please select a date range before downloading.");
      return;
    }

    setLoading(prev => ({ ...prev, [format]: true }));
    toast.loading(`Downloading ${format.toUpperCase()}...`, { id: "download" });

    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const PATIENT_ID = sessionStorage.getItem("_pt_id");

    const apiUrl = `${BASE_URL}/rpm/${account_id}/share-export-readings/${PATIENT_ID}/${format}/?start_date=${dateRange.start}&end_date=${dateRange.end}`;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": format === "pdf" ? "application/pdf" : "image/png"
        }
      });

      if (response.status === 404) {
        toast.error("No readings found for the selected period.");
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to download ${format.toUpperCase()}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const [endYear, endMonth] = dateRange.end.split("-");
      a.download = buildReportFilename({
        year: endYear,
        month: endMonth,
        patientFirstName: patient?.user?.first_name,
        patientLastName: patient?.user?.last_name,
        doctype: DOCTYPES.ALL_READINGS,
        program: "RPM",
        ext: format,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`${format.toUpperCase()} download complete!`);
    } catch (error) {
      console.error(`Error downloading ${format.toUpperCase()}:`, error);
      toast.error(
        `Failed to download ${format.toUpperCase()}. Please try again.`
      );
    } finally {
      setLoading(prev => ({ ...prev, [format]: false }));
      toast.dismiss("download");
    }
  };

  const handleCCMDownload = filename => {
    toast.success(`Downloading ${filename}...`);
    // Implement actual download logic for CCM documents
    // This is a placeholderd
  };

  const handleDocumentsUpload = () => {
    // Step 1: Check for filename matches
    if (uploadStep === 1) {
      const existingDocuments = patientDocumentsQuery.data || [];
      const matches = [];

      uploadedFiles.forEach(uploadDoc => {
        const existingDoc = existingDocuments.find(
          doc =>
            doc.document_name?.toLowerCase() ===
              uploadDoc.file.name.toLowerCase() &&
            doc.document_type?.toLowerCase() === uploadDoc.type.toLowerCase()
        );
        if (existingDoc) {
          matches.push({
            file: uploadDoc.file,
            type: uploadDoc.type,
            existingDoc: existingDoc,
            operation: "add" // default to 'add'
          });
        }
      });

      if (matches.length > 0) {
        // Show conflict resolution step
        setMatchedDocuments(matches);
        setUploadStep(2);
        return;
      }
    }

    // Step 2: Proceed with upload (either no matches or after conflict resolution)
    // Step 2: Proceed with upload (either no matches or after conflict resolution)

    // Filter out files that are in the matched list
    const newFiles = uploadedFiles.filter(
      file =>
        !matchedDocuments.some(
          match =>
            match.file.name === file.file.name &&
            match.type.toLowerCase() === file.type.toLowerCase()
        )
    );

    patientDocumentsMutation.mutate({
      newFiles,
      matchedFiles: matchedDocuments
    });
  };

  const handleShare = (format, provider) => {
    // toast.loading("Sharing Audit Report", { id: "audit-report" });
    setSharing(true);
    const iframe = document.createElement("iframe");
    // iframe.id = "audit-report-iframe";
    iframe.style.width = "100vw";
    iframe.style.height = "1px";
    iframe.style.position = "absolute";
    iframe.style.zIndex = "-10";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    let buttonInterval;
    iframe.onload = () => {
      buttonInterval = setInterval(() => {
        const downloadButton =
          iframe.contentDocument.getElementById("download-btn");

        if (downloadButton) {
          clearInterval(buttonInterval);
          downloadButton.click();
        }
      }, 200);
    };

    const date = `${year}-${month}`;
    iframe.src = `/billings/share-audit-report?date=${date}&patientId=${patientId}&email=${provider}`;

    setTimeout(() => {
      clearInterval(buttonInterval);

      // toast.dismiss("audit-report");
      setSharing(false);
      toast.success("Audit Report Shared");
      setIsShareingModalOpen(false);
    }, 8000);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 150000);
  };

  let documentTypes;

  if (RPM && CCM) {
    documentTypes = [
      { label: "Doctor Notes", value: "doctor" },
      { label: "Careplan", value: "careplan" },
      { label: "Consent", value: "consent" },
      { label: "Additional", value: "additional" },
      { label: "Appeal", value: "appeal" },
      { label: "Lab Reports", value: "lab" },
      { label: "Imaging", value: "imaging" }
    ];
  } else if (RPM) {
    documentTypes = [
      { label: "Consent", value: "consent" },
      { label: "Additional", value: "additional" },
      { label: "Appeal", value: "appeal" },
      { label: "Lab Reports", value: "lab" },
      { label: "Imaging", value: "imaging" }
      // { label: "All", value: "all" },
    ];
  } else if (CCM) {
    documentTypes = [
      { label: "Doctor Notes", value: "doctor" },
      { label: "Careplan", value: "careplan" },
      { label: "Additional", value: "additional" },
      { label: "Appeal", value: "appeal" },
      { label: "Lab Reports", value: "lab" },
      { label: "Imaging", value: "imaging" }
      // { label: "All", value: "all" },
    ];
  }

  // Render the RPM files container

  // const handleShare = (path) =>{
  //    const newTab  = window.open(path)
  //    newTab.onLoad = () =>{

  //    }
  // }

  const renderRPMContainer = () => (
    <section className={styles.download_wrapper}>
      <div className={styles.heading_cont}>
        <h2>Download Documents</h2>
      </div>

      <div className={styles.files_container}>
        <div className={styles.file_wrapper}>
          <div className={styles.left_cont}>
            <div className={styles.image_cont}>
              <img
                src={medicalReport || "/placeholder.svg"}
                alt="Medical Report"
              />
            </div>
            <div className={styles.statement_cont}>
              <h4>All Reading Reports</h4>
              <p>
                Select a range and get a report of all readings recorded within
                that time frame.
              </p>
            </div>
          </div>

          <div className={styles.right_cont}>
            <div className={styles.date_selecting}>
              <DateRangeSelector onDateChange={handleDateChange} />
            </div>
            <div className={styles.down_share_btns}>
              {/* <Dropdown
              onChange={(value) => handleDownload(readingDateRange, value)}
              trigger={
                <Button
                  variant="primary"
                  disabled={
                    loading.png ||
                    !readingDateRange.start ||
                    !readingDateRange.end
                  }
                  loading={loading.png}
                  className={styles.download_button}
                >
                  Download <FaFileDownload />
                </Button>
              }
            >
              {shareOptions.map((action, index) => (
                <DropdownItem
                  key={index}
                  value={action.value}
                  className={styles.dropdown_item}
                >
                  {action.icon}
                  {action.label}
                </DropdownItem>
              ))}
            </Dropdown> */}

              <Button
                onClick={() => handleDownload(readingDateRange, "pdf")}
                variant="primary"
                disabled={
                  loading.png ||
                  !readingDateRange.start ||
                  !readingDateRange.end
                }
                loading={loading.png}
                className={styles.btn}
              >
                Download
              </Button>

              <Button
                onClick={() => setIsOpen(true)}
                disabled={
                  loading.png ||
                  !readingDateRange.start ||
                  !readingDateRange.end
                }
                variant="primary"
                className={styles.btn}
                loading={loading.png}
              >
                Share <FaShare />
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.file_wrapper}>
          <div className={styles.left_cont}>
            <div className={styles.image_cont}>
              <img src={auditReport || "/placeholder.svg"} alt="Audit Report" />
            </div>
            <div className={styles.statement_cont}>
              <h4>Audit Report</h4>
              <p>
                Select a monthly/year and recive a monthly activity summary.
              </p>
            </div>
          </div>

          <div className={styles.right_cont}>
            <div className={styles.date_selecting}>
              <Select onChange={value => setMonth(value)} value={month}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month">
                    <p>
                      {months.find(m => m.value == month)?.label ??
                        "Select month"}
                      &nbsp;
                    </p>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  {(parseInt(year) < new Date().getFullYear()
                    ? months
                    : months.slice(0, new Date().getMonth() + 1)
                  ).map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select onChange={value => setYear(value)} value={year}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year">
                    {year ?? "Select year"}&nbsp;
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  {generateYears(2024)
                    .reverse()
                    .map(year => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className={styles.down_share_btns}>
              <Button
                size="small"
                className={styles.btn}
                disabled={!year || !month}
                onClick={() => {
                  navigate(
                    `/patient/audit-report?date=${year}-${month}&patientId=${patientId}`
                  );
                }}
              >
                Download <FaFileDownload />
              </Button>
              <Button
                size="small"
                disabled={!year || !month}
                onClick={() => setIsShareingModalOpen(true)}
                className={styles.btn}
              >
                Share <FaShare />
              </Button>
            </div>
          </div>
        </div>
        <Suspense fallback={<Spinner />}>
          <DownloadReadingDialogue
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            dateRange={{
              startDate: new Date(readingDateRange.start),
              endDate: new Date(readingDateRange.end)
            }}
          />
        </Suspense>
        <Suspense fallback={<Spinner />}>
          <SharingModal
            isOpen={isSharingModalOpen}
            setIsOpen={value => {
              setIsShareingModalOpen(value);
              setSharing(false);
            }}
            onShare={handleShare}
            isSharing={sharing}
            title="Share Documents"
            shareButtonText="Share Documents"
            showExternalProvider={true}
            showFormatSelector={true}
            availableFormats={["pdf", "png"]}
            defaultFormat="pdf"
            defaultShareType="provider"
            patientId={patientId}
          />
        </Suspense>
      </div>
    </section>
  );

  // Render the CCM container with the table component
  const renderCCMContainer = () => (
    <section className={styles.download_wrapper}>
      <div className={styles.heading_cont}>
        <h2>Download Documents</h2>
      </div>
      <div className={styles.ccm_container}>
        <div className={styles.ccm_table_wrapper}>
          <Table
            columns={ccmColumns}
            data={careplanLisTQuery?.data?.data}
            loading={false}
            height={400}
          />
        </div>
      </div>
    </section>
  );

  // Documents view
  const renderDocumentContainer = () => (
    <section className={styles.download_wrapper}>
      <div className={styles.heading_cont}>
        <h2>Documents</h2>
        <section>
          <Button onClick={() => setShowUploadModal(true)}>
            Upload Documents
          </Button>
        </section>
      </div>
      <div className={styles.ccm_container}>
        <div className={styles.document_container}>
          <section className={styles.document_filters}>
            {documentTypes?.map(({ label, value }) => (
              <Button
                key={value}
                isRound
                variant={
                  documentFilter.some(_filter => _filter === value)
                    ? "default"
                    : "ghost"
                }
                onClick={() => {
                  setDocumentFilter(prev =>
                    prev.includes(value)
                      ? prev.length > 1
                        ? prev.filter(v => v !== value)
                        : prev
                      : [...prev.filter(v => v !== "all"), value]
                  );
                }}
              >
                {label}
              </Button>
            ))}
            <Button
              key="all"
              isRound
              variant={
                documentFilter.some(_filter => _filter === "all")
                  ? "default"
                  : "ghost"
              }
              onClick={() => {
                setDocumentFilter(prev =>
                  prev.includes("all")
                    ? prev.length > 1
                      ? prev.filter(item => item !== "all")
                      : prev
                    : ["all"]
                );
              }}
            >
              All
            </Button>
          </section>

          <Table
            columns={patientDocumentsColumn}
            data={
              documentFilter.some(_filter => _filter === "all")
                ? patientDocumentsQuery.data
                : patientDocumentsQuery.data?.filter(
                    _doc =>
                      documentFilter.some(
                        _filter => _filter === _doc.document_type.toLowerCase()
                      )
                    // _doc.document_type.toLowerCase() === documentFilter
                  )
            }
            loading={false}
            height={400}
          />
        </div>
      </div>

      <Dialog
        open={showUploadModal}
        setOpen={() => {
          setShowUploadModal(false);
          setUploadStep(1);
          setMatchedDocuments([]);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {uploadStep === 1
                ? "Upload Documents"
                : "Resolve Document Conflicts"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {uploadStep === 1 ? (
              <section className={styles.upload_container}>
                <UploadFiles
                  accept=".pdf, .doc, .docx, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  showFileList={true}
                  placeholder="Drop your files here"
                  subText="Or click to browse"
                  buttonText="Browse"
                  filesHandler={files => setUploadedFiles(files)}
                  onSuccess={patientDocumentsMutation.isSuccess}
                />
              </section>
            ) : (
              <section className={styles.conflict_resolution_container}>
                <p className={styles.conflict_message}>
                  The following documents already exist. Choose an action for
                  each:
                </p>
                <div className={styles.matched_documents_table}>
                  <div className={styles.matched_documents_header}>
                    <div className={styles.header_cell}>Name</div>
                    <div className={styles.header_cell}>Type</div>
                    <div className={styles.header_cell}>Existing Document</div>
                    <div className={styles.header_cell}>Action</div>
                  </div>
                  <div className={styles.matched_documents_list}>
                    {matchedDocuments.map((match, index) => (
                      <div key={index} className={styles.matched_document_row}>
                        <div className={styles.document_cell}>
                          <div className={styles.document_name}>
                            {match.file.name}
                          </div>
                        </div>
                        <div className={styles.document_cell}>
                          <div className={styles.document_type}>
                            {match.type}
                          </div>
                        </div>
                        <div className={styles.document_cell}>
                          <div className={styles.existing_document_info}>
                            {formatDate(match.existingDoc.uploaded_at)} -{" "}
                            {match.existingDoc.document_type}
                          </div>
                        </div>
                        <div className={styles.document_cell}>
                          <div className={styles.operation_buttons}>
                            <Button
                              variant="primary"
                              isRound
                              loading={processingDocuments.some(
                                p => p.index === index && p.operation === "add"
                              )}
                              disabled={processingDocuments.some(
                                p => p.index === index
                              )}
                              onClick={async () => {
                                setProcessingDocuments(prev => [
                                  ...prev,
                                  { index, operation: "add" }
                                ]);
                                try {
                                  const formData = new FormData();
                                  formData.append("documents", match.file);
                                  formData.append("document_type", match.type);

                                  await duplicatePatientDocument(
                                    match.existingDoc.id,
                                    formData
                                  );

                                  toast.success(
                                    `${match.file.name} added successfully`
                                  );

                                  // Remove from matched documents and uploaded files
                                  setMatchedDocuments(prev =>
                                    prev.filter((_, i) => i !== index)
                                  );
                                  setUploadedFiles(prev =>
                                    prev.filter(
                                      f => f.file.name !== match.file.name
                                    )
                                  );

                                  queryClient.invalidateQueries([
                                    "patient-documents"
                                  ]);
                                } catch (error) {
                                  console.error("duplicate error:", error);
                                  toast.error(
                                    `Failed to add ${match.file.name}`
                                  );
                                } finally {
                                  setProcessingDocuments(prev =>
                                    prev.filter(
                                      p =>
                                        !(
                                          p.index === index &&
                                          p.operation === "add"
                                        )
                                    )
                                  );
                                }
                              }}
                              className={styles.operation_button}
                            >
                              Add
                            </Button>
                            <Button
                              variant="primary"
                              isRound
                              loading={processingDocuments.some(
                                p =>
                                  p.index === index && p.operation === "replace"
                              )}
                              disabled={processingDocuments.some(
                                p => p.index === index
                              )}
                              onClick={async () => {
                                setProcessingDocuments(prev => [
                                  ...prev,
                                  { index, operation: "replace" }
                                ]);
                                try {
                                  const formData = new FormData();
                                  formData.append("documents", match.file);
                                  formData.append("document_type", match.type);

                                  await replacePatientDocument(
                                    match.existingDoc.id,
                                    formData
                                  );

                                  toast.success(
                                    `${match.file.name} replaced successfully`
                                  );

                                  // Remove from matched documents and uploaded files
                                  setMatchedDocuments(prev =>
                                    prev.filter((_, i) => i !== index)
                                  );
                                  setUploadedFiles(prev =>
                                    prev.filter(
                                      f => f.file.name !== match.file.name
                                    )
                                  );

                                  queryClient.invalidateQueries([
                                    "patient-documents"
                                  ]);
                                } catch (error) {
                                  console.error("replace error:", error);
                                  toast.error(
                                    `Failed to replace ${match.file.name}`
                                  );
                                } finally {
                                  setProcessingDocuments(prev =>
                                    prev.filter(
                                      p =>
                                        !(
                                          p.index === index &&
                                          p.operation === "replace"
                                        )
                                    )
                                  );
                                }
                              }}
                              className={styles.operation_button}
                            >
                              Replace
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowUploadModal(false);
                setUploadStep(1);
                setMatchedDocuments([]);
                setProcessingDocuments([]);
              }}
            >
              Cancel
            </Button>
            {uploadStep === 1 ? (
              <Button
                disabled={uploadedFiles.length <= 0}
                loading={patientDocumentsMutation.isLoading}
                onClick={() => handleDocumentsUpload()}
              >
                Upload
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setUploadStep(1);
                    setMatchedDocuments([]);
                    setProcessingDocuments([]);
                  }}
                >
                  Back
                </Button>
                {matchedDocuments.length === 0 && uploadedFiles.length > 0 && (
                  <Button
                    loading={patientDocumentsMutation.isLoading}
                    onClick={() => handleDocumentsUpload()}
                  >
                    Upload Remaining
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Dialogue */}
      <AlertDialog
        open={showDeleteDialogue}
        setOpen={setShowDeleteDialogue}
        title="Delete Document"
        description="Are you sure you want to delete this document?"
      >
        <Button
          variant="normal"
          size="default"
          onClick={() => setShowDeleteDialogue(false)}
        >
          Cancel
        </Button>
        <Button
          loading={patientDocumenTDeleteMutation.isLoading}
          variant="danger"
          size="default"
          onClick={() => {
            patientDocumenTDeleteMutation.mutate(deleteDocumentId);
          }}
        >
          Delete
        </Button>
      </AlertDialog>
    </section>
  );

  // Render the toggler interface for patients with both classifications
  const renderToggler = () => (
    <div className={styles.toggler_container}>
      <div className={styles.toggler_buttons}>
        <button
          className={`${styles.toggler_btn} ${
            activeSection === "RPM"
              ? styles.toggler_btn_active
              : styles.toggler_btn_inactive
          }`}
          onClick={() => setActiveSection("RPM")}
        >
          RPM
        </button>
        <button
          className={`${styles.toggler_btn} ${
            activeSection === "CCM"
              ? styles.toggler_btn_active
              : styles.toggler_btn_inactive
          }`}
          onClick={() => setActiveSection("CCM")}
        >
          CCM
        </button>
      </div>

      {activeSection === "RPM" ? renderRPMContainer() : renderCCMContainer()}
    </div>
  );

  const renderContent = () => {
    if (RPM && CCM) {
      return renderToggler();
    } else if (RPM) {
      return renderRPMContainer();
    } else if (CCM) {
      return renderCCMContainer();
    } else {
      return renderRPMContainer();
    }
  };

  const handleDefaultTab = () =>
    RPM && CCM ? "RPM" : RPM ? "RPM" : CCM ? "CCM" : "RPM";

  return (
    <Tabs defaultValue={handleDefaultTab()}>
      <TabList>
        {RPM && CCM ? (
          <>
            <TabTrigger value="RPM">RPM</TabTrigger>
            <TabTrigger value="CCM">CCM</TabTrigger>
          </>
        ) : RPM ? (
          <TabTrigger value="RPM">RPM</TabTrigger>
        ) : CCM ? (
          <TabTrigger value="CCM">CCM</TabTrigger>
        ) : (
          <TabTrigger value="RPM">RPM</TabTrigger>
        )}
        <TabTrigger value="documents">Documents</TabTrigger>
      </TabList>
      <TabPanels>
        <Panel value="RPM">{renderRPMContainer()}</Panel>
        <Panel value="CCM">{renderCCMContainer()}</Panel>
        <Panel value="documents">{renderDocumentContainer()}</Panel>
      </TabPanels>
    </Tabs>
  );
};

export default PatientDocuments;
